// ============================================================
//  src/pages/auth/RegisterPage.js
// ============================================================
import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { registerUser as apiRegister } from '../../api/client';
import { getError } from '../../utils/helpers';
import './auth.css';

export default function RegisterPage() {
  const nav = useNavigate();
  const [form, setForm] = useState({
    fname: '', lname: '', email: '', phone: '', password: '',
  });
  const [showPwd, setShowPwd] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');

  const set = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true); setError('');
    try {
      const { data } = await apiRegister({
        fname:    form.fname.trim(),
        lname:    form.lname.trim(),
        email:    form.email.trim(),
        phone:    form.phone.trim(),
        password: form.password,
      });
      if (!data?.success) {
        setError(data?.message || 'Could not create your account.');
        return;
      }
      toast.success('Check your email for a verification code.');
      nav('/verify-email', { state: { email: data?.data?.email || form.email.trim() } });
    } catch (err) {
      setError(getError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-page">
      <div className="auth-left">
        <div className="auth-left-main">
          <div className="auth-brand">
            <span className="auth-brand-dot">C</span>
            <span>CommandKit</span>
          </div>
          <div>
            <div className="auth-headline">Join 1,000+<br /><em>bot makers.</em></div>
            <p className="auth-sub">
              Free to start. No credit card. Visual builder, real-time logs,
              and a marketplace for selling your bots.
            </p>
            <ul className="auth-features">
              <li><i className="fas fa-check" /> Free plan with 1 bot, 50 points/day</li>
              <li><i className="fas fa-check" /> Telegram and WhatsApp ready</li>
              <li><i className="fas fa-check" /> Python and JavaScript runtimes</li>
              <li><i className="fas fa-check" /> Deploy in seconds</li>
            </ul>
          </div>
          <div className="auth-footer-badge">
            <i className="fas fa-lock" /> 256-bit SSL · GDPR ready
          </div>
        </div>
      </div>

      <div className="auth-right">
        <div className="auth-shell">
          <div className="auth-mobile-brand">
            <span className="auth-brand-dot">C</span> CommandKit
          </div>

          <div className="auth-hdr">
            <h2>Create your account</h2>
            <p>It only takes a minute.</p>
          </div>

          {error && (
            <div className="auth-alert auth-alert-error">
              <i className="fas fa-circle-exclamation" /> {error}
            </div>
          )}

          <form onSubmit={submit} noValidate>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div className="auth-field" style={{ marginBottom: 0 }}>
                <label>First name</label>
                <div className="auth-field-wrap">
                  <i className="fas fa-user fi" />
                  <input type="text" value={form.fname} onChange={set('fname')} required />
                </div>
              </div>
              <div className="auth-field" style={{ marginBottom: 0 }}>
                <label>Last name</label>
                <div className="auth-field-wrap">
                  <i className="fas fa-user fi" />
                  <input type="text" value={form.lname} onChange={set('lname')} required />
                </div>
              </div>
            </div>

            <div className="auth-field" style={{ marginTop: 14 }}>
              <label>Email</label>
              <div className="auth-field-wrap">
                <i className="fas fa-envelope fi" />
                <input type="email" value={form.email} onChange={set('email')} placeholder="you@example.com" autoComplete="email" required />
              </div>
            </div>

            <div className="auth-field">
              <label>Phone</label>
              <div className="auth-field-wrap">
                <i className="fas fa-phone fi" />
                <input type="tel" value={form.phone} onChange={set('phone')} placeholder="+2348012345678" autoComplete="tel" required />
              </div>
            </div>

            <div className="auth-field">
              <label>Password</label>
              <div className="auth-field-wrap">
                <i className="fas fa-lock fi" />
                <input
                  type={showPwd ? 'text' : 'password'}
                  value={form.password}
                  onChange={set('password')}
                  placeholder="At least 6 characters"
                  autoComplete="new-password"
                  required
                />
                <button type="button" className="pw-toggle" onClick={() => setShowPwd((v) => !v)} tabIndex={-1}>
                  <i className={`fas ${showPwd ? 'fa-eye-slash' : 'fa-eye'}`} />
                </button>
              </div>
            </div>

            <button type="submit" className="btn-auth" disabled={loading}>
              {loading
                ? <><span className="spinner" /> Creating account…</>
                : <><i className="fas fa-user-plus" /> Create account</>
              }
            </button>

            <p style={{ fontSize: 12, color: 'var(--text-muted)', textAlign: 'center', marginTop: 14 }}>
              By creating an account you agree to our Terms and Privacy Policy.
            </p>
          </form>

          <div className="auth-bottom">
            Already have an account? <Link to="/login">Sign in</Link>
          </div>
        </div>
      </div>
    </div>
  );
}